import SEOHead from "@/components/base/SEOHead";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppFloat from "@/components/feature/WhatsAppFloat";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TrustBannerSection from "@/components/feature/TrustBannerSection";

export default function SobreNosotros() {
  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="Sobre Nosotros | Persianas Málaga"
        description="Más de 10 años reparando persianas, mosquiteras y motorización en Málaga capital. Técnicos en plantilla, factura y seguro, 2 años de garantía."
      />
      <Navbar />

      <Breadcrumb items={[
        { label: "Inicio", path: "/" },
        { label: "Sobre Nosotros" },
      ]} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Persianas Málaga",
        "description": "Empresa local de Málaga con más de 10 años de experiencia en reparación e instalación de persianas, mosquiteras y motorización. Técnicos en plantilla, factura y seguro, 2 años de garantía.",
        "telephone": "+34 951 12 07 70",
        "email": "info@persianas-malaga.es",
        "areaServed": "Málaga capital",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "287"
        }
      }) }} />

      {/* Hero */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-background-50 via-primary-50/30 to-accent-50/30">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
            <div className="flex-1 w-full">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-950 mb-4 leading-tight font-heading">
                Sobre <span className="text-primary-500">Nosotros</span>
              </h1>
              <p className="text-sm md:text-lg text-foreground-600 mb-6 max-w-lg leading-relaxed">
                Somos una empresa local de Málaga con más de 10 años de experiencia en reparación e instalación de persianas, mosquiteras y motorización. Técnicos especializados, factura y seguro, 2 años de garantía.
              </p>
            </div>
            <div className="w-full lg:w-[45%] h-64 sm:h-80 md:h-96 rounded-2xl overflow-hidden flex-shrink-0">
              <img alt="Equipo de Persianas Málaga" className="w-full h-full object-cover object-top" loading="eager" src="/images/015-sobre-nosotros-hero.webp" />
            </div>
          </div>
        </div>
      </section>

      {/* Historia y valores */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="px-4 md:px-6 max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 font-heading text-center">Nuestra historia en Málaga</h2>
          <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
            <p>
              <strong>Persianas Málaga</strong> nació hace más de una década con un objetivo claro: ofrecer un servicio técnico de reparación de persianas cercano, rápido y de confianza en Málaga capital. Desde el primer día, nuestra filosofía ha sido <strong>reparar antes que sustituir</strong>, ahorrando dinero a nuestros clientes y reduciendo residuos innecesarios.
            </p>
            <p>
              Empezamos siendo un pequeño equipo de dos técnicos recorriendo los barrios del centro de Málaga. Hoy, más de 10 años después, somos un equipo consolidado de <strong>técnicos especializados en plantilla</strong> — sin subcontratas — que damos servicio en los 16 barrios de Málaga capital, desde el Centro Histórico hasta Campanillas, desde Soliva hasta El Palo.
            </p>
            <p>
              Hemos realizado más de <strong>15.000 reparaciones e instalaciones</strong> en viviendas, comunidades de vecinos y locales comerciales de toda Málaga. Nuestra valoración media de 4.9 sobre 5 estrellas con más de 287 reseñas en Google es el mejor reflejo de nuestro compromiso con la calidad.
            </p>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-10 md:py-14 bg-gradient-to-br from-background-50 via-primary-50/15 to-accent-50/20">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 font-heading text-center">Nuestros valores</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { icon: "ri-hand-heart-line", title: "Cercanía", desc: "Somos de Málaga, vivimos en Málaga y conocemos cada barrio. Trato personal y directo con cada cliente." },
              { icon: "ri-scales-3-line", title: "Transparencia", desc: "Presupuesto cerrado antes de empezar. Sin sorpresas, sin letra pequeña, sin costes ocultos." },
              { icon: "ri-award-line", title: "Calidad", desc: "Solo utilizamos recambios originales de las mejores marcas. Formación continua de nuestros técnicos." },
              { icon: "ri-speed-up-line", title: "Rapidez", desc: "Respuesta en 24-48 horas en todos los barrios de Málaga. Para urgencias, same-day si llamas antes de las 12." },
            ].map((item) => (
              <div key={item.title} className="p-5 md:p-6 bg-background-50 rounded-2xl border border-background-200/60 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-4">
                  <span className="w-6 h-6 flex items-center justify-center"><i className={`${item.icon} text-primary-600 text-xl`}></i></span>
                </div>
                <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-1.5">{item.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compromisos */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Factura y seguro", text: "Somos una empresa legalmente establecida. Emitimos factura de todos nuestros trabajos y contamos con seguro de responsabilidad civil. Trabajar con profesionales te da tranquilidad." },
              { title: "Garantía de 2 años", text: "Todas nuestras reparaciones e instalaciones están cubiertas por una garantía de 2 años por escrito, incluyendo tanto las piezas sustituidas como la mano de obra." },
              { title: "Técnicos en plantilla", text: "No subcontratamos. Todos nuestros técnicos son personal contratado directamente por Persianas Málaga, formados internamente y con años de experiencia en reparación de persianas." },
            ].map((item) => (
              <div key={item.title} className="bg-gradient-to-br from-primary-500 to-primary-600 text-background-50 rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-bold mb-3 font-heading">{item.title}</h3>
                <p className="text-sm text-background-50/85 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 bg-gradient-to-r from-primary-500 to-primary-600">
        <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
          <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-3 font-heading">Habla con nosotros hoy</h3>
          <p className="text-sm md:text-base text-background-50/80 mb-6">Estaremos encantados de ayudarte con lo que necesites</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/contacto" className="inline-flex items-center justify-center gap-2 bg-background-50 hover:bg-background-100 text-primary-600 font-semibold text-sm px-8 py-4 rounded-full transition-all whitespace-nowrap cursor-pointer">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line text-base"></i></span>
              Ir a contacto
            </Link>
            <a href="tel:+34951120770" className="inline-flex items-center justify-center gap-2 bg-accent-400 hover:bg-accent-500 text-background-50 font-semibold text-sm px-8 py-4 rounded-full transition-all whitespace-nowrap cursor-pointer">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill text-base"></i></span>
              951 12 07 70
            </a>
          </div>
        </div>
      </section>

      <TrustBannerSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}